/**
 * Speech-owned morph teardown (Aug 16 2026).
 *
 * The Visual IVR / live speech path writes viseme frames DIRECTLY onto
 * `morphTargetInfluences` (no mixer tracks), and the F4/M4 "speech-restore"
 * loop in Avatar.tsx copies these morphs from a donor submesh (teeth — no
 * mixer bindings) back onto the body mesh every frame. Neither the timeout
 * cleanup nor the barge-in `avatar:stop` path ever reset those values, so a
 * speech animation cancelled mid-viseme froze the mouth at the last frame —
 * typically jawOpen mid-vowel. The donor copy then re-applied the stale open
 * jaw indefinitely, which read as "mouth hanging open while thinking".
 *
 * `zeroSpeechOwnedMorphs` zeroes the speech-owned set on EVERY morph mesh in
 * the scene (body + donors). It must run on both speech-teardown paths:
 * the natural-completion `cleanup()` and the barge-in `handleStopAvatar`.
 * Mood/idle overlays re-apply their own values on the next tick, so this
 * never fights the mood animator.
 */

import { resolveMorphTargetIndex } from '../converter';

// The body mesh is the ONLY morph mesh with idle/mixer track bindings —
// they sample tracks that pull face morphs back to ~0, overriding the
// speech RAF's writes. The other morph submeshes (SK_F4_Head_3 = teeth,
// etc.) have NO mixer track bindings, so they retain the speech RAF's
// writes intact; the speech-restore loop uses them as the source of truth
// and copies mouth/jaw morphs back to the body mesh per frame. Eye/brow/
// cheek-squint morphs are NOT included so blink and brow expressions keep
// working.
//
// Includes both ARKit camelCase (jawOpen) and underscore aliases (jaw_L)
// because different rigs name them differently and the speech RAF resolves
// via getMorphTargets() which expands across both.
export const SPEECH_OWNED_MORPH_NAMES: ReadonlyArray<string> = [
  'jawOpen', 'jawForward', 'jawLeft', 'jawRight',
  'jaw_L', 'jaw_R',
  'mouthClose', 'mouthFunnel', 'mouthPucker', 'mouthOpen',
  'mouthLeft', 'mouthRight', 'mouth_L', 'mouth_R',
  'mouthSmileLeft', 'mouthSmileRight', 'mouthSmile_L', 'mouthSmile_R',
  'mouthFrownLeft', 'mouthFrownRight', 'mouthFrown_L', 'mouthFrown_R',
  'mouthDimpleLeft', 'mouthDimpleRight', 'mouthDimple_L', 'mouthDimple_R',
  'mouthStretchLeft', 'mouthStretchRight', 'mouthStretch_L', 'mouthStretch_R',
  'mouthRollLower', 'mouthRollUpper',
  'mouthShrugLower', 'mouthShrugUpper',
  'mouthPressLeft', 'mouthPressRight', 'mouthPress_L', 'mouthPress_R',
  'mouthLowerDownLeft', 'mouthLowerDownRight', 'mouthLowerDown_L', 'mouthLowerDown_R',
  'mouthUpperUpLeft', 'mouthUpperUpRight', 'mouthUpperUp_L', 'mouthUpperUp_R',
  'cheekPuff',
  'tongueOut', 'tongue_out',
];

type TraversableRoot = {
  traverse: (callback: (node: unknown) => void) => void;
};

/**
 * Zero the speech-owned morphs on every morph mesh under `root` (body AND
 * viseme-donor submeshes). Returns the number of influences changed, which
 * is useful for logging and tests.
 */
export function zeroSpeechOwnedMorphs(root: TraversableRoot | null | undefined): number {
  if (!root || typeof root.traverse !== 'function') {
    return 0;
  }
  let zeroed = 0;
  root.traverse((node: any) => {
    const influences = node?.morphTargetInfluences as number[] | undefined;
    const dict = node?.morphTargetDictionary as Record<string, number> | undefined;
    if (!influences || !dict) return;
    for (const morphName of SPEECH_OWNED_MORPH_NAMES) {
      const idx = resolveMorphTargetIndex(dict, morphName);
      if (idx !== undefined && idx < influences.length && influences[idx] !== 0) {
        influences[idx] = 0;
        zeroed++;
      }
    }
  });
  return zeroed;
}
